/* eslint-disable no-lonely-if */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import DxpPageShell from "../Common/StandaloneFormViews/DxpPageShell";
import {
  createDxpClickHandler,
  getCompanyPartnerLevel,
  getDXPRegistrationLink,
  getStoredMembershipType,
  getSupportCaseLink,
  persistPartnerLevel,
} from "../../Helpers/utils";
import { PROGRAM_SPECIFIC_NAMES } from "../../Helpers/constants";
import { persistDxpRegistrationResumeFromPartnerApi } from "../../Helpers/hydratePartnerRegistrationSession";
import { displayToasts } from "../../Helpers/displayAppToast";
import useBackButtonOverride from "../../Hooks/useBackButtonOverride";
import { getPartnerRegistrationDetails } from "../../Services/PartnerRegistrationAPI";
import {
  setDxpUserProfileDraft,
  setPartnerRegistrationFormStep,
  setSelectedRegisterCompanyType,
} from "../../Redux/actions";
import {
  DxpBodyText,
  DxpFooterRow,
  DxpHeading,
  DxpMessageCard,
  DxpNarrowColumn,
  DxpPrimaryButton,
} from "../dxpStandaloneUi";
import DxpRegistrationTopHeader from "../DxpRegistrationTopHeader";

interface IRegistrationSuccessMessage {
  type?: string;
  appType?: string;
}

const PROGRAM = "DXP";

function SaveExitView({
  appType,
  customHandlers,
}: {
  appType?: string;
  customHandlers: Record<string, (props: unknown) => void>;
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const email = localStorage.getItem("email") || "";
  const [continueBusy, setContinueBusy] = useState(false);

  const handleContinueNow = async () => {
    if (continueBusy) return;
    const partnerId = localStorage.getItem("accountId");
    if (!partnerId || partnerId === "undefined") {
      displayToasts({
        type: "error",
        text: "We could not find your saved registration. Please use the link in your email to continue.",
        autoClose: true,
      });
      return;
    }
    setContinueBusy(true);
    try {
      const ok = await persistDxpRegistrationResumeFromPartnerApi(partnerId);
      if (!ok) {
        displayToasts({
          type: "error",
          text: "Unable to load your saved application. Please try again.",
          autoClose: true,
        });
        return;
      }

      const companyType = localStorage.getItem("selectedRegisterCompanyType");
      if (companyType) {
        dispatch(setSelectedRegisterCompanyType(companyType));
      }
      const draftRaw = sessionStorage.getItem("dxpUserProfileDraft");
      if (draftRaw) {
        try {
          dispatch(setDxpUserProfileDraft(JSON.parse(draftRaw)));
        } catch {
          dispatch(setDxpUserProfileDraft(null));
        }
      } else {
        dispatch(setDxpUserProfileDraft(null));
      }

      dispatch(setPartnerRegistrationFormStep(4));
      navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.registration);
    } finally {
      setContinueBusy(false);
    }
  };

  return (
    <>
      <DxpRegistrationTopHeader program={PROGRAM} />

      <DxpPageShell
        appType={appType}
        customHandlers={customHandlers}
        requireCommonHeaders
      >
        <div className="dxp-static-message-outer">
          <DxpNarrowColumn>
            <DxpMessageCard>
              <DxpHeading>Your application was saved!</DxpHeading>
              <DxpBodyText
                html={`Your application has been saved. We've sent you an email at <span class='dxp-text-strong-dark'>${email}</span> with steps to follow to return to your saved registration form.`}
              />
              <DxpBodyText>
                You can go to the portal at any time and sign in to continue
                registering.
              </DxpBodyText>
              <DxpFooterRow>
                <DxpPrimaryButton
                  label="Continue now"
                  onPress={() => {
                    handleContinueNow().catch(() => undefined);
                  }}
                  variant="accent"
                  className="dxp-wizard-cta-inverse"
                />
              </DxpFooterRow>
            </DxpMessageCard>
          </DxpNarrowColumn>
        </div>
      </DxpPageShell>
    </>
  );
}

function ContinueRegistrationView({
  appType,
  customHandlers,
}: {
  appType?: string;
  customHandlers: Record<string, (props: unknown) => void>;
}) {
  const onAction = useMemo(
    () => createDxpClickHandler(customHandlers),
    [customHandlers],
  );
  const email = localStorage.getItem("email") || "";

  return (
    <>
      <DxpRegistrationTopHeader program={PROGRAM} />

      <DxpPageShell
        appType={appType}
        customHandlers={customHandlers}
        requireCommonHeaders
      >
        <div className="dxp-static-message-outer">
          <DxpNarrowColumn>
            <DxpMessageCard>
              <DxpHeading>Welcome back! Continue your registration</DxpHeading>
              <DxpBodyText
                html={`Great news, since you already have started the registration process with <span class='dxp-text-strong-dark'>${email}</span>, you can continue from where you left off. To complete your registration, please sign in using this Adobe ID.`}
              />
              <div className="dxp-footer-actions-row-end">
                <DxpPrimaryButton
                  label="Sign in"
                  onPress={() =>
                    onAction({ actionType: "continueRegistration" })
                  }
                  variant="accent"
                  className="dxp-wizard-cta-inverse"
                />
                <DxpPrimaryButton
                  label="Complete registration now"
                  onPress={() =>
                    onAction({ actionType: "continueRegistration" })
                  }
                  variant="accent"
                  className="dxp-wizard-cta-inverse"
                />
              </div>
            </DxpMessageCard>
          </DxpNarrowColumn>
        </div>
      </DxpPageShell>
    </>
  );
}

function DynamicSubmittedView({
  appType,
  mainMessageHtml,
  subMessageHtml,
  customHandlers,
}: {
  appType?: string;
  mainMessageHtml: string;
  subMessageHtml: string;
  customHandlers: Record<string, (props: unknown) => void>;
}) {
  const onAction = useMemo(
    () => createDxpClickHandler(customHandlers),
    [customHandlers],
  );

  return (
    <>
      <DxpRegistrationTopHeader program={PROGRAM} />

      <DxpPageShell
        appType={appType}
        customHandlers={customHandlers}
        requireCommonHeaders
      >
        <div className="dxp-static-message-outer">
          <DxpNarrowColumn>
            <DxpMessageCard className="dxp-message-card--min-h-350">
              <DxpHeading>
                Welcome to the Adobe Digital Experience Partner Program!
              </DxpHeading>
              <DxpBodyText html={mainMessageHtml} />
              <DxpBodyText html={subMessageHtml} />
              <DxpFooterRow>
                <DxpPrimaryButton
                  label="Explore now"
                  onPress={() => onAction({ actionType: "DXPHomePage" })}
                  variant="accent"
                  className="dxp-wizard-cta-inverse"
                />
              </DxpFooterRow>
            </DxpMessageCard>
          </DxpNarrowColumn>
        </div>
      </DxpPageShell>
    </>
  );
}

function RegistrationSuccessMessage({
  type,
  appType,
}: IRegistrationSuccessMessage) {
  useBackButtonOverride(getDXPRegistrationLink());

  const accountIdRef = useRef(localStorage.getItem("accountId"));
  const accountId = accountIdRef.current;
  const accountStatus = localStorage.getItem("accountStatus");
  const selectedRegisterCompanyType = localStorage.getItem(
    "selectedRegisterCompanyType",
  );
  const companyName = localStorage.getItem("companyName");
  const [membershipType, setMembershipType] = useState(getStoredMembershipType);

  useEffect(() => {
    if (type === "saveExit" || type === "continueRegistration") {
      return undefined;
    }

    const accId = accountIdRef.current;
    if (accId && accId !== "undefined") {
      localStorage.removeItem("accountId");
    }

    if (membershipType || !accId || accId === "undefined") return undefined;

    let cancelled = false;
    (async () => {
      const result: { status?: number; data?: unknown } =
        await getPartnerRegistrationDetails(
          PROGRAM_SPECIFIC_NAMES[PROGRAM].programType,
          accId,
        );
      if (cancelled || result?.status !== 1) return;
      const level = getCompanyPartnerLevel(result.data);
      if (!level) return;
      persistPartnerLevel({ level });
      setMembershipType(level);
    })().catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [type, membershipType]);
  const parentCompanyName = localStorage.getItem("affiliatedCompanyName");
  const parentCompanyLevel = localStorage.getItem("parentCompanyLevel");
  const adminName = localStorage.getItem("adminName");
  const adminEmail = localStorage.getItem("adminEmail");

  const replacePlaceholders = (
    text: string,
    useParentLevel = false,
  ): string => {
    let partnerLevel = "[partner level]";
    if (useParentLevel && parentCompanyLevel) {
      partnerLevel = `<b>${parentCompanyLevel}</b>`;
    } else if (membershipType) {
      partnerLevel = `<b>${membershipType}</b>`;
    }
    return text
      .replace(
        /\[company\]/g,
        companyName ? `<b>${companyName}</b>` : "[company]",
      )
      .replace(/\[partner level\]/g, partnerLevel)
      .replace(
        /\[parent company\]/g,
        parentCompanyName ? `<b>${parentCompanyName}</b>` : "[parent company]",
      )
      .replace(/\[Name\]/g, adminName ? `<b>${adminName}</b>` : "[Name]")
      .replace(/\[email\]/g, adminEmail ? `<b>${adminEmail}</b>` : "[email]");
  };

  const getDynamicContent = () => {
    if (type && type === "saveExit") {
      return null;
    }
    if (type && type === "continueRegistration") {
      return null;
    }
    if (accountId && accountId !== "undefined") {
      if (accountStatus) {
        if (accountStatus === "Approved") {
          return {
            mainMessage: replacePlaceholders(
              "As a new user with [company], you will have access to all [partner level] content.",
            ),
            subMessage:
              "Click the button below to start exploring exclusive partner content and benefits.",
            statusMessage:
              "User registers as a new user with a company that is already a Dx member.<br/>Company status: Active",
          };
        }
        if (accountStatus === "Submitted In Review") {
          return {
            mainMessage: replacePlaceholders(
              "As a new user with [company], you typically have access to all [partner level] content. However, your company request to join the program as a [partner level] partner is now under review to ensure all requirements are met. Once your company is approved you will be able to fully access the partner portal content.",
            ),
            subMessage:
              "In the meantime, please click the button below to access the partner portal, but keep in mind that some content may be restricted until your company application review is completed.",
            statusMessage:
              "User registers as a new user with a company that is already a Dx member.<br/>Company status: Submitted In Review",
          };
        }
        if (accountStatus === "Missing Payment") {
          return {
            mainMessage: replacePlaceholders(
              `As a new user with [company], you will typically have access to all [partner level] content. However, we have not yet received your company's membership fee, which means your access will be limited until the fee has been paid. For information about payment status, please contact our <a href='${getSupportCaseLink()}' target='_blank' rel='noopener noreferrer' class='dxp-text-accent-link'>partner support team</a>.`,
            ),
            subMessage:
              "In the meantime, please use the button below to access available content.",
            statusMessage:
              "User registers as a new user with a company that is already a Dx member.<br/>Company status: Locked > payment issues",
          };
        }
        if (accountStatus === "Locked") {
          return {
            mainMessage: replacePlaceholders(
              `As a new user with [company], you will typically have access to all [partner level] content. However, your company has a pending compliance requirement that means your access will be limited until the requirement is fulfilled. For information about the status of the compliance requirement, please contact our <a href='${getSupportCaseLink()}' target='_blank' rel='noopener noreferrer' class='dxp-text-accent-link'>partner support team</a>.`,
            ),
            subMessage:
              "In the meantime, please use the button below to access available content.",
            statusMessage:
              "User registers as a new user with a company that is already a Dx member.<br/>Company status: Locked > compliance issues",
          };
        }
      }
      if (selectedRegisterCompanyType === "subsidaryCompany") {
        return {
          mainMessage: replacePlaceholders(
            "As a new user with [company], your application is now under review to ensure all requirements are met. You can expect to receive an email confirmation of your membership status within 3-5 business days.",
          ),
          subMessage: replacePlaceholders(
            "As a subsidiary of [parent company], you will inherit your parent company's [partner level] partner benefits.<br/><br/>In the meantime, please click the button below to access the partner portal, but keep in mind that some content may be restricted until your application review is completed.",
            true,
          ),
          statusMessage:
            "User registers as a new user with a company that is not a Dx member.<br/>Applies as a subsidiary as an existing member account",
        };
      }

      return {
        mainMessage: replacePlaceholders(
          "As a new user with [company], you will have access to all [partner level] content.",
        ),
        subMessage:
          "Click the button below to start exploring exclusive partner content and benefits.",
        statusMessage:
          "User registers as a new user with a company that is already a Dx member.<br/>Company status: Active",
      };
    }
    if (selectedRegisterCompanyType === "subsidaryCompany") {
      return {
        mainMessage: replacePlaceholders(
          "As a new user with [company], your application is now under review to ensure all requirements are met. You can expect to receive an email confirmation of your membership status within 3-5 business days.",
        ),
        subMessage: replacePlaceholders(
          "As a subsidiary of [parent company], you will inherit your parent company's [partner level] partner benefits.<br/><br/>In the meantime, please click the button below to access the partner portal, but keep in mind that some content may be restricted until your application review is completed.",
          true,
        ),
        statusMessage:
          "User registers as a new user with a company that is not a Dx member.<br/>Applies as a subsidiary as an existing member account",
      };
    }

    if (membershipType === "Community") {
      return {
        mainMessage: replacePlaceholders(
          "As a new user with [company], your request to join the program as a [partner level] partner is now under review to ensure all requirements are met. You can expect to receive an email confirmation of your membership status within 3-5 business days.",
        ),
        subMessage:
          "In the meantime, please click the button below to start exploring exclusive partner content and benefits.",
        statusMessage:
          "User registers as a new user with a company that is not a Dx member.<br/>Applies for a new company (Community)",
      };
    }
    if (membershipType === "Silver") {
      return {
        mainMessage: replacePlaceholders(
          "As a new user with [company], your request to join the program as a [partner level] partner is now under review to ensure all requirements are met. You can expect to receive an email confirmation of your membership status within 3-5 business days.",
        ),
        subMessage:
          "In the meantime, please click the button below to access the partner portal, but keep in mind that some content may be restricted until your application review is completed.",
        statusMessage:
          "User registers as a new user with a company that is not a Dx member.<br/>Applies for a new company (Silver)",
      };
    }

    return {
      mainMessage: replacePlaceholders(
        "As a new user with [company], your request to join the program as a [partner level] partner is now under review to ensure all requirements are met. You can expect to receive an email confirmation of your membership status within 3-5 business days.",
      ),
      subMessage:
        "In the meantime, please click the button below to start exploring exclusive partner content and benefits.",
      statusMessage:
        "User registers as a new user with a company that is not a Dx member.<br/>Applies for a new company",
    };
  };

  const dynamicContent = getDynamicContent();

  if (
    type !== "saveExit" &&
    type !== "continueRegistration" &&
    dynamicContent
  ) {
    localStorage.setItem("mainMessage", dynamicContent.mainMessage);
    localStorage.setItem("subMessage", dynamicContent.subMessage);
    localStorage.setItem("statusMessage", dynamicContent.statusMessage);
  }

  const customHandlers = useMemo(
    () => ({
      navigateManageProfile: () => {
        localStorage.removeItem("emailMismatch");
        localStorage.removeItem("partnerRegistrationFormStep");
        window.open(
          PROGRAM_SPECIFIC_NAMES.DXP.manageProfile.personalInfo,
          "_self",
        );
      },
    }),
    [],
  );

  const defaultHandler = useMemo(
    () =>
      ({
        ...customHandlers,
      }) as Record<string, (props: unknown) => void>,
    [customHandlers],
  );

  if (type === "saveExit") {
    return <SaveExitView appType={appType} customHandlers={defaultHandler} />;
  }

  if (type === "continueRegistration") {
    return (
      <ContinueRegistrationView
        appType={appType}
        customHandlers={defaultHandler}
      />
    );
  }

  return (
    <DynamicSubmittedView
      appType={appType}
      mainMessageHtml={dynamicContent?.mainMessage || ""}
      subMessageHtml={dynamicContent?.subMessage || ""}
      customHandlers={defaultHandler}
    />
  );
}

export default RegistrationSuccessMessage;
